import { useCallback } from 'react';
import { useRouter } from 'next/router';

import redirects from '@zg-rentals/auth-comet/redirects';
import { buildMfaStepUpRetryHandler, useClearMfaRetryParam } from '@zg-rentals/login-zillow/client';

import { useIsInMobileWebView } from '../../hooks/useIsInMobileWebView';

import { EXTERNAL_ROUTES, ROUTES } from '../../constants/urls';

import { Auth, useAuth } from '../../components/AuthProvider';

import type React from 'react';

const APP_NAME = 'zillow-rental-manager-web';

type RedirectHrefFn = (href: string, params: URLSearchParams) => string;

export type AuthCheckWrapperProps = {
  children: React.ReactNode;
  /**
   * Optional custom redirect when unauthenticated. When provided, skips default logic
   * (__DEV__ → featureFlags, mobile native sign-in, signInPage). Use for page-specific returnUrl.
   */
  redirectHref?: RedirectHrefFn;
  /** Forwarded to Auth.Boundary */
  loading: React.ReactNode;
  /** Forwarded to Auth.Boundary */
  redirecting: React.ReactNode;
  /** Forwarded to Auth.Boundary */
  onError?: React.ReactNode | ((reason: string) => React.ReactNode);
  /** Forwarded to Auth.Boundary */
  onUnauthorized?: React.ReactNode | ((user: unknown) => React.ReactNode);
};

/**
 * Wraps children with Auth.Boundary for Next.js app pages. When the user is not authenticated,
 * redirects to a login page. Pass redirectHref to override with a custom URL (e.g. returnUrl only).
 */
const AuthCheckWrapper = ({
  children,
  redirectHref,
  loading,
  redirecting,
  onError,
  onUnauthorized,
}: AuthCheckWrapperProps) => {
  const router = useRouter();

  const isInMobileWebView = useIsInMobileWebView();

  const { user } = useAuth();

  useClearMfaRetryParam({ appName: APP_NAME, user });

  const resolveRedirectHref = useCallback<RedirectHrefFn>(
    (href, params) => {
      if (redirectHref) {
        return redirectHref(href, params);
      }

      if (__DEV__) {
        return `/rental-manager${ROUTES.FEATURE_FLAGS}`;
      }

      const manifest = redirects.zillow;
      const isMfaRequired = user?.isInitialMultiFactorAuthenticationRequired;
      params.delete('mfaStepUp');
      const search = params.size ? `?${params.toString()}` : '';

      if (isInMobileWebView && 'nativeAppSignInPage' in manifest) {
        const redirectTo = manifest.nativeAppSignInPage?.(href, search);
        if (redirectTo) {
          return redirectTo;
        }
      }

      const loginUrl = manifest.signInPage(href, search, EXTERNAL_ROUTES.SIGN_IN);
      return isMfaRequired ? `${loginUrl}${loginUrl.includes('?') ? '&' : '?'}mfaStepUp=true` : loginUrl;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      redirectHref,
      isInMobileWebView,
      router.pathname,
      router.asPath,
      router.basePath,
      user?.isInitialMultiFactorAuthenticationRequired,
    ],
  );

  const handleUnauthorized = buildMfaStepUpRetryHandler({
    appName: APP_NAME,
    buildNumber: process?.env?.NEXT_PUBLIC_BUILD_NUMBER,
    user,
    redirectHref: resolveRedirectHref,
    fallback: onUnauthorized,
    redirecting,
  });

  return (
    <Auth.Boundary
      redirectHref={resolveRedirectHref}
      loading={loading}
      redirecting={redirecting}
      onError={onError}
      onUnauthorized={handleUnauthorized}
    >
      {children}
    </Auth.Boundary>
  );
};

export default AuthCheckWrapper;
